export const DEFAULT_FILTER_DRAFT = [[{ id: '0', fn: '', ft: '', dt: '', dj: '', c: '', v: '' }]];

export const createDefaultFilterDraft = () =>
  (typeof structuredClone === 'function'
    ? structuredClone(DEFAULT_FILTER_DRAFT)
    : JSON.parse(JSON.stringify(DEFAULT_FILTER_DRAFT)));

export const deepCopy = (value) =>
  (typeof structuredClone === 'function'
    ? structuredClone(value)
    : JSON.parse(JSON.stringify(value)));

export const isFilterRowActive = (row) => {
  if (!row || typeof row !== 'object') {
    return false;
  }

  const fieldName = String(row?.fn ?? row?.FieldName ?? row?.field ?? '').trim();
  const comparator = String(row?.c ?? row?.op ?? row?.operator ?? '').trim();
  if (!fieldName || !comparator) {
    return false;
  }

  if (comparator === 'isEmpty') {
    return true;
  }

  const value = row?.v;
  return !(value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0));
};

export const sanitizeFilterDraft = (filters) => {
  if (!Array.isArray(filters)) {
    return [];
  }

  return filters
    .map((group) => (Array.isArray(group) ? group.filter(isFilterRowActive) : []))
    .filter((group) => group.length > 0);
};

export const compactFilterDraft = (filters) => {
  if (!Array.isArray(filters)) {
    return [];
  }

  return filters.map((group) =>
    (Array.isArray(group) ? group : []).map((row) => ({
      id: row?.id ?? '',
      fn: row?.fn ?? '',
      ft: row?.ft ?? '',
      dt: row?.dt ?? '',
      c: row?.c ?? '',
      v: row?.v ?? null,
    }))
  );
};

export const uiToServerMethod = (comparator) => {
  switch (comparator) {
    case 'contains':
      return 'Contains';
    case 'startsWith':
      return 'StartsWith';
    case 'endsWith':
      return 'EndsWith';
    case 'strictMatch':
      return '==';
    default:
      return comparator;
  }
};

const makeFilterLeaf = (row) => ({
  Name: row.fn,
  Method: uiToServerMethod(row.c),
  Value: row.v,
  CheckCase: false,
  Left: null,
  Right: null,
});

const makeFilterConjunction = (left, op, right) => ({
  Name: null,
  Method: op,
  Value: null,
  CheckCase: false,
  Left: left,
  Right: right,
});

const expandIsEmptyFilter = (row) => {
  const nullCheck = { Name: row.fn, Method: '==', Value: null, CheckCase: false, Left: null, Right: null };
  const emptyCheck = { Name: row.fn, Method: '==', Value: '', CheckCase: false, Left: null, Right: null };
  return makeFilterConjunction(nullCheck, '||', emptyCheck);
};

const foldFilterNodes = (nodes, op) => {
  if (!Array.isArray(nodes) || nodes.length === 0) {
    return null;
  }

  let node = nodes[0];
  for (let i = 1; i < nodes.length; i += 1) {
    node = makeFilterConjunction(node, op, nodes[i]);
  }

  return node;
};

export const buildDashboardFilterTree = (filters) => {
  const groups = Array.isArray(filters) ? filters : [];
  const groupTrees = groups
    .map((group) => {
      const usableRows = (Array.isArray(group) ? group : []).filter(isFilterRowActive);
      if (usableRows.length === 0) {
        return null;
      }

      const nodes = usableRows.map((row) => (row.c === 'isEmpty' ? expandIsEmptyFilter(row) : makeFilterLeaf(row)));
      return foldFilterNodes(nodes, '&&');
    })
    .filter(Boolean);

  return foldFilterNodes(groupTrees, '||');
};

export const normalizeDashboardQueryState = (queryState = {}, overrides = {}) => {
  const rawFilterDraft = Array.isArray(overrides?.FilterDraft ?? queryState?.FilterDraft)
    ? deepCopy(overrides?.FilterDraft ?? queryState?.FilterDraft)
    : [];
  const sanitizedFilterDraft = sanitizeFilterDraft(rawFilterDraft);
  const persistedFilterDraft = compactFilterDraft(rawFilterDraft);
  const groupByColumns = Array.isArray(overrides?.GroupByColumns ?? queryState?.GroupByColumns)
    ? [...(overrides?.GroupByColumns ?? queryState?.GroupByColumns)]
    : [];
  const measureNames = Array.isArray(overrides?.MeasureNames ?? queryState?.MeasureNames)
    ? [...(overrides?.MeasureNames ?? queryState?.MeasureNames)]
    : [];
  const visualAggregatesSource =
    overrides?.QueryAggregates
    ?? queryState?.QueryAggregates
    ?? overrides?.VisualAggregates
    ?? queryState?.VisualAggregates;
  const visualAggregates = Array.isArray(visualAggregatesSource)
    ? deepCopy(visualAggregatesSource)
    : [];
  const explicitSupportsRealTime = overrides?.SupportsRealTime ?? queryState?.SupportsRealTime;
  const supportsRealTime =
    explicitSupportsRealTime === undefined || explicitSupportsRealTime === null
      ? (groupByColumns.length === 0 && measureNames.length === 0 && visualAggregates.length === 0)
      : !!explicitSupportsRealTime;
  const normalizedGroupByColumns = supportsRealTime ? [] : groupByColumns;
  const normalizedMeasureNames = supportsRealTime ? [] : measureNames;
  const normalizedVisualAggregates = supportsRealTime ? [] : visualAggregates;
  const hasGroupedQueryShape =
    normalizedGroupByColumns.length > 0
    || normalizedMeasureNames.length > 0
    || normalizedVisualAggregates.length > 0;

  return {
    ...(queryState ?? {}),
    ...(overrides ?? {}),
    QueryMode: hasGroupedQueryShape ? 2 : Number(overrides?.QueryMode ?? queryState?.QueryMode ?? 1) || 1,
    SemanticShape: hasGroupedQueryShape ? 2 : Number(overrides?.SemanticShape ?? queryState?.SemanticShape ?? 1) || 1,
    SupportsRealTime: supportsRealTime,
    SelectColumns: Array.isArray(overrides?.SelectColumns ?? queryState?.SelectColumns)
      ? [...(overrides?.SelectColumns ?? queryState?.SelectColumns)]
      : [],
    GroupByColumns: normalizedGroupByColumns,
    MeasureNames: normalizedMeasureNames,
    QueryAggregates: normalizedVisualAggregates,
    VisualAggregates: normalizedVisualAggregates,
    FilterDraft: persistedFilterDraft,
    RootFilterNode: sanitizedFilterDraft.length > 0 ? buildDashboardFilterTree(sanitizedFilterDraft) : null,
    Sorts: Array.isArray(overrides?.Sorts ?? queryState?.Sorts)
      ? deepCopy(overrides?.Sorts ?? queryState?.Sorts)
      : [],
    Groupings: Array.isArray(overrides?.Groupings ?? queryState?.Groupings)
      ? deepCopy(overrides?.Groupings ?? queryState?.Groupings)
      : [],
    VisibleColumns: Array.isArray(overrides?.VisibleColumns ?? queryState?.VisibleColumns)
      ? [...(overrides?.VisibleColumns ?? queryState?.VisibleColumns)]
      : [],
  };
};

const quoteIdent = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

const escapeString = (value) => String(value ?? '').replace(/'/g, "''");

const toSqlLiteral = (value) => {
  if (value === null || value === undefined) {
    return 'NULL';
  }

  if (typeof value === 'boolean') {
    return value ? 'TRUE' : 'FALSE';
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  return `'${escapeString(value)}'`;
};

export const buildWhereClauseFromDashboardFilterNode = (node) => {
  if (!node || (node?.Name == null && node?.Left == null && node?.Right == null)) {
    return '';
  }

  const method = String(node?.Method ?? '');
  if (method === '&&' || method === '||') {
    const left = buildWhereClauseFromDashboardFilterNode(node?.Left);
    const right = buildWhereClauseFromDashboardFilterNode(node?.Right);
    if (!left) {
      return right;
    }
    if (!right) {
      return left;
    }

    return `(${left} ${method === '||' ? 'OR' : 'AND'} ${right})`;
  }

  const column = String(node?.Name ?? '').trim();
  if (!column) {
    return '';
  }

  const ident = quoteIdent(column);
  const value = node?.Value;

  switch (method) {
    case 'Contains':
      return `${ident} LIKE ${toSqlLiteral(`%${value ?? ''}%`)}`;
    case 'StartsWith':
      return `${ident} LIKE ${toSqlLiteral(`${value ?? ''}%`)}`;
    case 'EndsWith':
      return `${ident} LIKE ${toSqlLiteral(`%${value ?? ''}`)}`;
    case '==':
      return value == null ? `${ident} IS NULL` : `${ident} = ${toSqlLiteral(value)}`;
    case '!=':
      return value == null ? `${ident} IS NOT NULL` : `${ident} <> ${toSqlLiteral(value)}`;
    case '>':
    case '>=':
    case '<':
    case '<=':
      return `${ident} ${method} ${toSqlLiteral(value)}`;
    default:
      return '';
  }
};
